import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useMemo } from "react";

const NAMESPACE = "bingo";
const SEPARATOR = "/";

/**
 * Tracks when a user last viewed a specific piece of content, identified by a
 * key. Returns an array containing the timestamp of last view (if any) and a
 * callback to mark the key as viewed.
 *
 * If the user has viewed a piece of content, an ISO timestamp is returned of
 * the last time `setLastReadNow` was invoked.
 *
 * If the user has not read a piece of content, the timestamp is undefined.
 */
export function useLastViewedTimestamp(key: string | string[]) {
  const [current, setInLocalStorage] = useLocalStorage<string>(
    useMemo(() => toLocalStorageKey(key), [key]),
  );

  const setLastReadNow = useCallback(() => {
    const updated = new Date().toISOString();

    setInLocalStorage(updated);
  }, [setInLocalStorage]);

  return [current, setLastReadNow] as const;
}

function toLocalStorageKey(key: string | string[]) {
  if (typeof key === "string") {
    return [NAMESPACE, key].join(SEPARATOR);
  }
  return [NAMESPACE, ...key].join(SEPARATOR);
}
